import { Component, Vue, Prop, Watch, Inject } from 'vue-property-decorator';
import { INoviNsPaymentGlobals } from '../../common/types/Payment/NoviNsPaymentGlobals';
import { loadStripe, StripeError, StripeElements, Stripe, ConfirmationToken, ConfirmationTokenResult, PaymentIntent, StripeElementsOptionsClientSecret, CreateConfirmationToken, StripeElementsOptionsMode } from "@stripe/stripe-js";
import { AchTokenData, createAchTokenData, AutoPayOption, CreditCardTokenData, createCreditCardTokenData, PaymentProcessorOptions } from '@/common/types/AllTypesExports';
import { RandomGenerator } from '../../common/services/RandomGenerator';
import { PropOptions } from 'vue';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';

declare let noviNs: INoviNsPaymentGlobals;

@Component({
    name: 'StripePaymentForm',
    components: {
        FieldValidationErrors
    }
})
export default class StripePaymentFormClass extends Vue {

    @Prop({ required: true } as PropOptions<PaymentProcessorOptions>)
    processorOptions!: PaymentProcessorOptions;

    @Prop({ required: false, default: false })
    isAch!: boolean;

    @Prop({ required: false, default: false })
    isCard!: boolean;

    @Prop({ required: false, default: () => false })
    forceSave!: boolean;

    @Prop({ required: false, default: () => true })
    validate!: boolean;

    achData: AchTokenData = createAchTokenData();
    creditCardData: CreditCardTokenData = createCreditCardTokenData();

    stripeFactory!: Stripe;
    paymentElement!: StripeElements | null;
    paymentIntent!: PaymentIntent;
    elementId!: string;

    @Inject('validator')
    $validator: any;

    get saveAchOption() {
        return this.isAch && this.processorOptions.enableSaveAch && !noviNs.isAddPaymentMethod;
    }

    get saveCardOption() {
        return this.isCard && this.processorOptions.enableSaveCreditCard && !noviNs.isAddPaymentMethod;
    }

    @Watch('forceSave', { immediate: true })
    onForceSave(forceSave: boolean) {
        if (this.saveAchOption && forceSave) {
            this.achData.saveAch = true;
        }

        if (this.saveCardOption && forceSave) {
            this.creditCardData.saveCard = true;
        }
    }

    created() {
        this.elementId = `stripe-payment-container-${RandomGenerator.generateRandomString()}`;
    }

    mounted() {
        loadStripe(noviNs.stripeKey).then((stripe: Stripe | null) => {
            if (stripe != null) {
                this.stripeFactory = stripe;
                const options = <StripeElementsOptionsMode>{
                    paymentMethodCreation: 'manual',
                    paymentMethodTypes: this.getPaymentType(),
                    currency: "usd",
                    onBehalfOf: noviNs.noviPayAccountId
                };

                if (!noviNs.enableNoviPayManualACH) {
                    options.paymentMethodOptions = {
                        us_bank_account: { verification_method: 'instant' }
                    }
                }

                if (noviNs.isAddPaymentMethod) {
                    options.mode = 'setup';
                } else {
                    options.mode = 'payment';
                    options.amount = 50; // min amount on init
                }

                this.paymentElement = this.stripeFactory.elements(options);
                this.paymentElement.create('payment').mount(`#${this.elementId}`);
            }
        });    
    }

    createStripePaymentToken(autoPayOptions: AutoPayOption[], billablePartyId: number, selectedPaymentPlanId?: number) {
        if (this.paymentElement != null) {
            this.paymentElement.submit().then((submitResult) => {
                if (submitResult.error != undefined && submitResult.error != null) {
                    this.emitError(`There was an issue processing your payment. Details: ${submitResult.error.message}`);
                } else {
                    this.stripeFactory.createConfirmationToken(<CreateConfirmationToken>{ elements: this.paymentElement }).then((result: ConfirmationTokenResult) => {
                        if (result.error != undefined && result.error != null) {
                            console.log(result.error);
                            this.emitError(`There was an issue processing your payment. Details: ${result.error}`);
                        } else {

                            if (this.isAch) {
                                this.achData.confirmationToken = result.confirmationToken.id;
                                this.achData.autoPayOptions = autoPayOptions;
                                this.achData.billablePartyId = billablePartyId;
                                this.achData.paymentProcessorModule = noviNs.paymentProcessor;
                                this.emitAchPaymentToken(this.achData);
                            }
                            else if (this.isCard) {
                                this.creditCardData.confirmationToken = result.confirmationToken.id;
                                this.creditCardData.autoPayOptions = autoPayOptions;
                                this.creditCardData.billablePartyId = billablePartyId;
                                this.creditCardData.paymentProcessorModule = noviNs.paymentProcessor;
                                this.creditCardData.selectedPaymentPlanId = selectedPaymentPlanId ?? 0;
                                this.emitCardPaymentToken(this.creditCardData);
                            }
                        }
                    });    
                }
            })
            .catch((error: string) => {
                this.emitError(`There was an issue processing your payment. Details: ${error}`);
            });
        }
    }

    emitAchPaymentToken(achEntry: AchTokenData) {
        
        this.$emit('on-confirm-token-ready', achEntry);
    }

    emitCardPaymentToken(cardEntry: CreditCardTokenData) {

        this.$emit('on-payment-token-ready', cardEntry);
    }

    emitError(errorMsg: string) {
        this.$emit('on-error-toggle', true, errorMsg);
    }

    // We only want one payment type to be selected at a time. Never return more than 1 type.
    getPaymentType() {
        if (this.isAch) {
            return ["us_bank_account"];
        }

        if (this.isCard) {
            return ["card"];
        }
    }
}